import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocalizedPricingPlans, type PricingPlan } from '../../utils/localizedPlans';

interface CompactPlanPickerProps {
  plans: PricingPlan[];
  onChoose: (plan: PricingPlan) => void;
}

function parseAmount(price?: string): number {
  return Number(String(price || '').replace(/[^0-9.]/g, '')) || 0;
}

function planTheme(name: string, isPopular?: boolean) {
  const n = String(name || '').toLowerCase();
  if (isPopular) {
    return {
      ring: '#3B82F6',
      surface: 'linear-gradient(160deg, #EFF6FF 0%, #FFFFFF 72%)',
      price: '#1D4ED8',
      chip: '#DBEAFE',
      chipText: '#1E40AF',
    };
  }
  if (n.includes('platinum')) {
    return {
      ring: '#7C3AED',
      surface: 'linear-gradient(160deg, #F5F3FF 0%, #FFFFFF 72%)',
      price: '#6D28D9',
      chip: '#EDE9FE',
      chipText: '#5B21B6',
    };
  }
  if (n.includes('gold')) {
    return {
      ring: '#D97706',
      surface: 'linear-gradient(160deg, #FFFBEB 0%, #FFFFFF 72%)',
      price: '#B45309',
      chip: '#FEF3C7',
      chipText: '#92400E',
    };
  }
  if (n.includes('free')) {
    return {
      ring: '#10B981',
      surface: 'linear-gradient(160deg, #ECFDF5 0%, #FFFFFF 72%)',
      price: '#047857',
      chip: '#D1FAE5',
      chipText: '#065F46',
    };
  }
  return {
    ring: '#CBD5E1',
    surface: '#FFFFFF',
    price: '#0F172A',
    chip: '#F1F5F9',
    chipText: '#475569',
  };
}

export const CompactPlanPicker: React.FC<CompactPlanPickerProps> = ({ plans, onChoose }) => {
  const { t } = useTranslation();
  const localizedPlans = useLocalizedPricingPlans(plans);

  if (localizedPlans.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '32px 16px', color: '#94A3B8', fontSize: 14 }}>
        {t('auth_plan_none', { defaultValue: 'No plans available right now.' })}
      </div>
    );
  }

  return (
    <div>
      <p
        style={{
          margin: '0 0 18px',
          textAlign: 'center',
          fontSize: 14,
          lineHeight: 1.5,
          color: '#64748B',
        }}
      >
        {t('mws_plan_hint', { defaultValue: 'Select a plan to continue. Upgrade or change anytime from your dashboard.' })}
      </p>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))',
          gap: 14,
        }}
      >
        {localizedPlans.map((plan, idx) => {
          const theme = planTheme(plan.name, plan.isPopular);
          const isFree = parseAmount(plan.price) === 0;
          const highlights = (Array.isArray(plan.features) ? plan.features : []).slice(0, 2);
          const extraCount = Math.max(0, (plan.features?.length || 0) - highlights.length);

          return (
            <button
              key={plan.id || idx}
              type="button"
              onClick={() => onChoose(plan)}
              aria-label={`${t('auth_plan_choose', { defaultValue: 'Choose' })} ${plan.name}`}
              style={{
                position: 'relative',
                textAlign: 'left',
                padding: '18px 18px 16px',
                borderRadius: 16,
                border: `1.5px solid ${plan.isPopular ? theme.ring : '#E2E8F0'}`,
                background: theme.surface,
                cursor: 'pointer',
                transition: 'transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease',
                boxShadow: plan.isPopular ? '0 10px 28px rgba(59,130,246,0.12)' : '0 2px 8px rgba(15,23,42,0.04)',
                display: 'flex',
                flexDirection: 'column',
                minHeight: 196,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = plan.isPopular
                  ? '0 14px 32px rgba(59,130,246,0.16)'
                  : '0 8px 24px rgba(15,23,42,0.08)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = plan.isPopular
                  ? '0 10px 28px rgba(59,130,246,0.12)'
                  : '0 2px 8px rgba(15,23,42,0.04)';
              }}
            >
              {plan.isPopular && (
                <span
                  style={{
                    position: 'absolute',
                    top: 12,
                    right: 12,
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: 0.4,
                    textTransform: 'uppercase',
                    padding: '4px 8px',
                    borderRadius: 999,
                    background: theme.chip,
                    color: theme.chipText,
                  }}
                >
                  {t('auth_plan_popular', { defaultValue: 'Popular' })}
                </span>
              )}

              <div style={{ fontSize: 15, fontWeight: 700, color: '#0F172A', paddingRight: plan.isPopular ? 56 : 0 }}>
                {plan.name}
              </div>

              <div style={{ marginTop: 10, display: 'flex', alignItems: 'baseline', gap: 6 }}>
                <span style={{ fontSize: 28, fontWeight: 800, color: theme.price, lineHeight: 1 }}>
                  {plan.price}
                </span>
                {!isFree && (
                  <span style={{ fontSize: 12, color: '#94A3B8', fontWeight: 500 }}>
                    {t('mws_plan_per_month', { defaultValue: '/mo' })}
                  </span>
                )}
              </div>

              {plan.description && (
                <p
                  style={{
                    margin: '10px 0 0',
                    fontSize: 12,
                    lineHeight: 1.45,
                    color: '#64748B',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                >
                  {plan.description}
                </p>
              )}

              {highlights.length > 0 && (
                <ul style={{ listStyle: 'none', margin: '12px 0 0', padding: 0, flex: 1 }}>
                  {highlights.map((feature, i) => (
                    <li
                      key={i}
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 8,
                        fontSize: 11,
                        lineHeight: 1.4,
                        color: '#475569',
                        marginBottom: i === highlights.length - 1 ? 0 : 6,
                      }}
                    >
                      <span style={{ color: '#10B981', fontWeight: 700, flexShrink: 0 }}>✓</span>
                      <span
                        style={{
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}
                      >
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>
              )}

              <div
                style={{
                  marginTop: 14,
                  paddingTop: 12,
                  borderTop: '1px solid rgba(226,232,240,0.9)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 8,
                }}
              >
                <span style={{ fontSize: 12, fontWeight: 700, color: theme.price }}>
                  {isFree
                    ? t('mws_plan_start_free', { defaultValue: 'Start free' })
                    : t('mws_plan_select', { defaultValue: 'Select plan' })}
                </span>
                {extraCount > 0 && (
                  <span style={{ fontSize: 10, color: '#94A3B8' }}>
                    +{extraCount} {t('mws_plan_more_short', { defaultValue: 'more' })}
                  </span>
                )}
              </div>
            </button>
          );
        })}
      </div>

      <p style={{ margin: '16px 0 0', textAlign: 'center', fontSize: 11, color: '#94A3B8' }}>
        {t('mws_plan_footer', { defaultValue: '14-day trial included · Cancel anytime' })}
      </p>
    </div>
  );
};

export default CompactPlanPicker;
